import { FC, useMemo } from "react";
import { MDSButton, MDSButtonVariant, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import { RecallPermissionKind } from "@/modules/electron/types";
import {
  ConfigurationList,
  ConfigurationListItem,
  ConfigurationListItemKind,
  ConfigurationPermissionStatus,
} from "@/components/configuration";
import {
  StepTitle,
  StepContent,
  FooterContainer,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.styles";

type Props = {
  onNext: () => void;
  onSkip: () => void;
  restartRequired: boolean;
  resolveConfigurationStatus: ({
    kind,
  }: {
    kind: RecallPermissionKind;
  }) => ConfigurationPermissionStatus;
  handleEnablePermission: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  handleOpenPermissionPane: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
};

export const OnboardingVoicePermissionsScreen: FC<Props> = ({
  onNext,
  onSkip,
  restartRequired,
  resolveConfigurationStatus,
  handleEnablePermission,
  handleOpenPermissionPane,
}) => {
  const microphoneStatus = resolveConfigurationStatus({ kind: RecallPermissionKind.Microphone });

  const systemAudioStatus = resolveConfigurationStatus({
    kind: RecallPermissionKind.SystemAudio,
  });

  const isMicrophoneGranted = microphoneStatus === ConfigurationPermissionStatus.Granted;
  const isSystemAudioGranted = systemAudioStatus === ConfigurationPermissionStatus.Granted;

  const isMicrophoneLoading = microphoneStatus === ConfigurationPermissionStatus.Loading;
  const isSystemAudioLoading = systemAudioStatus === ConfigurationPermissionStatus.Loading;

  const isAnyLoading = isMicrophoneLoading || isSystemAudioLoading;
  const allPermissionsGranted = isMicrophoneGranted && isSystemAudioGranted;

  /**
   * We let the user proceed as soon as they've requested all permissions.
   */
  const canProceed = (() => {
    if (isAnyLoading) {
      return false;
    }

    if (restartRequired) {
      return true;
    }

    if (allPermissionsGranted) {
      return true;
    }

    return false;
  })();

  const items = useMemo<ConfigurationListItem[]>(() => {
    const itemList: ConfigurationListItem[] = [
      {
        kind: ConfigurationListItemKind.Permission,
        id: "microphone",
        title: "Microphone",
        description: "Mem relies on your microphone to capture your voice",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.Microphone }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.Microphone,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.Microphone,
          }),
      },
      {
        kind: ConfigurationListItemKind.Divider,
        id: "divider-screen-recording",
      },
      {
        kind: ConfigurationListItemKind.Permission,
        id: "screen-recording",
        title: "System audio recording only",
        description:
          "Mem relies on this permission to capture other people's voices during meetings",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.SystemAudio }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.SystemAudio,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.SystemAudio,
          }),
      },
    ];

    return itemList;
  }, [handleEnablePermission, handleOpenPermissionPane, resolveConfigurationStatus]);

  return (
    <WizardContentWrapper>
      <StepTitle>Set up Voice Mode</StepTitle>

      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          Mem can record and create notes from meeting audio, podcasts, or simply your own voice.
          Grant the required permissions in your system settings.
        </MDSText>

        <ConfigurationList items={items} />
      </StepContent>

      <FooterContainer align={allPermissionsGranted ? "flex-end" : "space-between"}>
        {!allPermissionsGranted && (
          <MDSButton variant={MDSButtonVariant.Transparent} onClick={onSkip}>
            I don&apos;t want to use Voice Mode
          </MDSButton>
        )}
        <MDSButton variant={MDSButtonVariant.Contrast} onClick={onNext} isDisabled={!canProceed}>
          {allPermissionsGranted ? "Finish" : "Next"}
        </MDSButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
};
